import type { GeocodedLead, DensityCluster, RouteStop } from '../types/visitRoute';

const EARTH_RADIUS_KM = 6371;

/**
 * Calcola la distanza in km tra due punti usando la formula di Haversine
 */
export function haversineDistance(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Ordina i lead per distanza da un punto di origine
 */
export function sortByProximity(
  origin: { lat: number; lng: number },
  leads: GeocodedLead[]
): (GeocodedLead & { distance: number })[] {
  return leads
    .map(lead => ({
      ...lead,
      distance: haversineDistance(origin.lat, origin.lng, lead.lat, lead.lng),
    }))
    .sort((a, b) => a.distance - b.distance);
}

/**
 * Approssimazione TSP con nearest-neighbor greedy.
 * Parte dall'origin e ad ogni passo sceglie il candidato più vicino.
 */
export function nearestNeighborRoute(
  origin: GeocodedLead,
  candidates: GeocodedLead[]
): GeocodedLead[] {
  const remaining = [...candidates];
  const route: GeocodedLead[] = [];
  let current = origin;

  while (remaining.length > 0) {
    let nearestIdx = 0;
    let nearestDist = Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const dist = haversineDistance(current.lat, current.lng, remaining[i].lat, remaining[i].lng);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestIdx = i;
      }
    }

    current = remaining[nearestIdx];
    route.push(current);
    remaining.splice(nearestIdx, 1);
  }

  return route;
}

/**
 * Genera un URL Google Maps con percorso multi-stop.
 * Google Maps URL supporta fino a ~10 waypoint nel URL.
 */
export function buildGoogleMapsUrl(stops: { lat: number; lng: number }[]): string {
  if (stops.length === 0) return '';
  if (stops.length === 1) {
    return `https://www.google.com/maps/dir/?api=1&destination=${stops[0].lat},${stops[0].lng}`;
  }

  const origin = stops[0];
  const destination = stops[stops.length - 1];
  const waypoints = stops.slice(1, -1);

  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lng}&destination=${destination.lat},${destination.lng}`;

  if (waypoints.length > 0) {
    const waypointStr = waypoints.map(w => `${w.lat},${w.lng}`).join('|');
    url += `&waypoints=${encodeURIComponent(waypointStr)}`;
  }

  url += '&travelmode=driving';
  return url;
}

/**
 * Costruisce una DirectionsRequest per Google Maps Directions API.
 * Max 25 waypoint (origin + destination + 23 intermediate).
 */
export function buildDirectionsRequest(
  stops: { lat: number; lng: number }[]
): google.maps.DirectionsRequest | null {
  if (stops.length < 2) return null;

  const origin = stops[0];
  const destination = stops[stops.length - 1];
  const waypoints = stops.slice(1, -1).map(s => ({
    location: new google.maps.LatLng(s.lat, s.lng),
    stopover: true,
  }));

  return {
    origin: new google.maps.LatLng(origin.lat, origin.lng),
    destination: new google.maps.LatLng(destination.lat, destination.lng),
    waypoints: waypoints.slice(0, 23), // Max 23 intermediate
    optimizeWaypoints: true,
    travelMode: google.maps.TravelMode.DRIVING,
  };
}

/**
 * Stima se un percorso rientra nel budget temporale.
 * Usa velocità media 40 km/h come euristica per spostamenti urbani/extraurbani.
 */
export function estimateRouteFeasibility(
  stops: GeocodedLead[],
  maxHours: number,
  visitDurationMin: number
): { feasibleStops: GeocodedLead[]; totalTimeMin: number } {
  const AVG_SPEED_KMH = 40;
  const maxMin = maxHours * 60;
  let totalTimeMin = 0;
  const feasibleStops: GeocodedLead[] = [];

  for (let i = 0; i < stops.length; i++) {
    // Tempo di viaggio dalla tappa precedente (o dall'origin per la prima)
    if (i > 0) {
      const dist = haversineDistance(
        stops[i - 1].lat, stops[i - 1].lng,
        stops[i].lat, stops[i].lng
      );
      totalTimeMin += (dist / AVG_SPEED_KMH) * 60;
    }

    // Tempo della visita
    totalTimeMin += visitDurationMin;

    if (totalTimeMin > maxMin) break;
    feasibleStops.push(stops[i]);
  }

  return { feasibleStops, totalTimeMin };
}

/**
 * Trova cluster ad alta densità di lead usando una griglia semplice.
 */
export function findHighDensityClusters(
  leads: GeocodedLead[],
  gridSizeKm: number = 5,
  minLeadsPerCluster: number = 3
): DensityCluster[] {
  if (leads.length === 0) return [];

  // Approssimazione: 1 grado lat ≈ 111 km, 1 grado lng ≈ 111 * cos(lat) km
  const avgLat = leads.reduce((s, l) => s + l.lat, 0) / leads.length;
  const latStep = gridSizeKm / 111;
  const lngStep = gridSizeKm / (111 * Math.cos(toRad(avgLat)));

  const grid = new Map<string, GeocodedLead[]>();

  for (const lead of leads) {
    const gridRow = Math.floor(lead.lat / latStep);
    const gridCol = Math.floor(lead.lng / lngStep);
    const key = `${gridRow}_${gridCol}`;
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key)!.push(lead);
  }

  const clusters: DensityCluster[] = [];
  for (const [gridKey, clusterLeads] of grid) {
    if (clusterLeads.length >= minLeadsPerCluster) {
      const centerLat = clusterLeads.reduce((s, l) => s + l.lat, 0) / clusterLeads.length;
      const centerLng = clusterLeads.reduce((s, l) => s + l.lng, 0) / clusterLeads.length;
      clusters.push({
        centerLat,
        centerLng,
        leadCount: clusterLeads.length,
        leads: clusterLeads,
        gridKey,
      });
    }
  }

  return clusters.sort((a, b) => b.leadCount - a.leadCount);
}

/**
 * Calcola distanze e tempi tra tappe consecutive per il pannello dettagli.
 */
export function computeStopDistances(
  stops: RouteStop[]
): RouteStop[] {
  return stops.map((stop, i) => {
    if (i === 0) {
      return { ...stop, distance_from_prev_km: 0, travel_time_from_prev_min: 0 };
    }
    const prev = stops[i - 1];
    const dist = haversineDistance(prev.lat, prev.lng, stop.lat, stop.lng);
    const travelMin = Math.round((dist / 40) * 60); // 40 km/h media
    return {
      ...stop,
      distance_from_prev_km: Math.round(dist * 10) / 10,
      travel_time_from_prev_min: travelMin,
    };
  });
}

/**
 * Costruisce l'indirizzo completo da un lead per il geocoding
 */
export function buildFullAddress(lead: {
  street_address?: string;
  street_number?: string;
  postal_code?: string;
  city?: string;
  province?: string;
  country?: string;
}): { address: string; isFullAddress: boolean } {
  const parts: string[] = [];
  const hasStreet = !!lead.street_address?.trim();

  if (hasStreet) {
    let street = lead.street_address!.trim();
    if (lead.street_number?.trim()) street += ' ' + lead.street_number.trim();
    parts.push(street);
  }

  const cityParts: string[] = [];
  if (lead.postal_code?.trim()) cityParts.push(lead.postal_code.trim());
  if (lead.city?.trim()) cityParts.push(lead.city.trim());
  if (lead.province?.trim()) cityParts.push(lead.province.trim());
  if (cityParts.length > 0) parts.push(cityParts.join(' '));

  if (lead.country?.trim()) parts.push(lead.country.trim());

  return {
    address: parts.join(', '),
    isFullAddress: hasStreet && !!lead.city?.trim(),
  };
}
